import { ChangeEvent, useEffect, useState } from 'react';

import { PossibleBannerFields } from '../BannerDetails/BannerDetails';
import { LabelInput, NewRow, SpeechBubble } from '../../shared';
import { LanguageType } from '@lifeserver/shared';

import { useBannerTranslation } from '../../hooks/banners';

import { languages } from '../../utils/utils';
import { hasSubtitle } from '../utils';

import { Banner, BannerTranslationsType } from '../../types/bannerTypes';

import { EmptyBanner } from '../emptyBanner';
import styles from './BannerTranslations.module.scss';

type BannerTranslationsProps = {
    language: LanguageType;
    banner: Banner | EmptyBanner;
    onInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onTranslationsLoaded: (translations: BannerTranslationsType) => void;
};

export const BannerTranslations = (props: BannerTranslationsProps) => {
    const { language, banner, onInputChange, onTranslationsLoaded } = props;
    const { translations, getBannerTranslations, isLoading, errorMessage } =
        useBannerTranslation(banner.stringId);
    const [isMessageShown, setIsMessageShown] = useState<boolean>(false);

    const getTranslationStatusMessage = (): string => {
        if (errorMessage) {
            return `That didn't work. Sorry! Server says: ${errorMessage}`;
        }

        if (isLoading) {
            return 'Loading from google...';
        }

        if (translations) {
            return "I've updated the text for you!";
        }

        return '';
    };

    useEffect(() => {
        if (translations) {
            onTranslationsLoaded(translations);
        }
    }, [translations]);

    useEffect(() => {
        if (isLoading) {
            setIsMessageShown(true);
        } else {
            setTimeout(() => {
                setIsMessageShown(false);
            }, 5000);
        }
    }, [isLoading]);

    return (
        <>
            <div className={styles.rowWrapper}>
                <LabelInput
                    className={styles.noMarginBottom}
                    size="semiwide"
                    name={PossibleBannerFields.StringId}
                    value={banner.stringId || ''}
                    tooltip="String ID that used for loading translations"
                    label="String ID"
                    onChange={onInputChange}
                />
                {isMessageShown ? (
                    <SpeechBubble
                        style={{
                            justifyContent: 'left',
                            marginLeft: 0,
                            marginRight: 0,
                        }}
                    >
                        {getTranslationStatusMessage()}
                    </SpeechBubble>
                ) : (
                    <button
                        disabled={!banner.stringId}
                        onClick={getBannerTranslations}
                    >
                        Load from Google Sheet
                    </button>
                )}
            </div>
            <NewRow />
            <LabelInput
                size="semiwide"
                name={PossibleBannerFields.Title}
                value={banner.title[language] || ''}
                tooltip=""
                label="Banner title in "
                labelHighlight={languages[language]}
                onChange={onInputChange}
            />
            {hasSubtitle(banner) && (
                <LabelInput
                    size="semiwide"
                    name={PossibleBannerFields.Subtitle}
                    value={
                        banner.subtitle ? banner.subtitle[language] || '' : ''
                    }
                    tooltip="Optional"
                    label="Banner subtitle in "
                    labelHighlight={languages[language]}
                    onChange={onInputChange}
                />
            )}
        </>
    );
};
